import { ApiError, type BranchSummary } from '../lib/api.js';
import { el, formatRelativeTime, mount } from '../lib/dom.js';

export interface BranchListSource {
  load(): Promise<{ branches: BranchSummary[]; protectedBranch: string }>;
  delete(branches: string[]): Promise<{
    results: { branch: string; ok: boolean; error: string | null }[];
  }>;
}

export interface BranchListSlots {
  /** Controls rendered on their own row between the title and the toolbar. */
  headerControls?: HTMLElement[];
  /** Extra buttons inserted at the start of the toolbar (left of "Delete selected"). */
  toolbarLeading?: HTMLElement[];
  /** Extra buttons inserted at the end of the toolbar (right of summary, before Refresh). */
  toolbarTrailing?: HTMLElement[];
  /** Per-row action buttons. Receives the branch and a `refresh()` callback. */
  rowActions?: (branch: BranchSummary, refresh: () => Promise<void>) => HTMLElement[];
  /** Per-row meta line override. Default = "{relativeTime} · {author} · {message}". */
  rowMeta?: (branch: BranchSummary) => string | HTMLElement;
  /** Extra badges rendered inline after the branch name. Nulls are skipped. */
  rowBadges?: (branch: BranchSummary) => (HTMLElement | null)[];
}

export interface BranchListController {
  root: HTMLElement;
  refresh: () => Promise<void>;
  /** Re-renders the rows from the branches already loaded (no network call). */
  rerender: () => void;
  showAlert: (message: string, kind?: 'error' | 'success') => void;
  getBranches: () => readonly BranchSummary[];
  getSelected: () => readonly string[];
  clearSelection: () => void;
}

export interface CreateBranchListOptions {
  title: string;
  source: BranchListSource;
  slots?: BranchListSlots;
  emptyMessage?: string;
  /** Fires whenever the set of selected (non-protected) branches changes. */
  onSelectionChange?: (selected: readonly string[]) => void;
}

/**
 * Generic branches list with checkbox selection and bulk delete.
 * Used by both /branches (app repo) and /runpod-images (image repo).
 *
 * The bulk-delete button and per-row delete button are always rendered;
 * extra actions (Deploy, Merge, Build…) come from `slots`.
 */
export function createBranchList(opts: CreateBranchListOptions): BranchListController {
  const list = el('div', { class: 'branch-list' });
  const summary = el('span', { class: 'who' });
  const refreshBtn = el('button', { text: 'Refresh', class: 'ghost' });
  const deleteSelectedBtn = el('button', {
    class: 'danger',
    text: 'Delete selected',
    attrs: { disabled: true },
  });
  const alertBox = el('div', { class: 'alert', attrs: { hidden: true, role: 'alert' } });

  const selected = new Set<string>();
  let branches: BranchSummary[] = [];
  let protectedBranch = 'main';
  let loaded = false;

  const showAlert = (message: string, kind: 'error' | 'success' = 'error') => {
    alertBox.className = kind === 'success' ? 'alert success' : 'alert';
    alertBox.textContent = message;
    alertBox.removeAttribute('hidden');
    setTimeout(() => alertBox.setAttribute('hidden', ''), 6000);
  };

  const getSelected = (): string[] => [...selected].filter((b) => b !== protectedBranch);

  const updateButtons = () => {
    const deletable = getSelected();
    deleteSelectedBtn.toggleAttribute('disabled', deletable.length === 0);
    opts.onSelectionChange?.(deletable);
  };

  const renderRow = (b: BranchSummary): HTMLElement => {
    const checkbox = el('input', {
      attrs: {
        type: 'checkbox',
        'aria-label': `Select ${b.name}`,
        disabled: b.isProtected,
      },
      on: {
        change: () => {
          if (checkbox.checked) selected.add(b.name);
          else selected.delete(b.name);
          updateButtons();
        },
      },
    });
    if (selected.has(b.name)) checkbox.checked = true;

    const deleteBtn = el('button', {
      class: 'danger',
      text: 'Delete',
      attrs: { disabled: b.isProtected },
      on: {
        click: async () => {
          if (b.isProtected) return;
          if (!confirm(`Delete branch "${b.name}"? This cannot be undone.`)) return;
          deleteBtn.setAttribute('disabled', '');
          try {
            await opts.source.delete([b.name]);
            await refresh();
            showAlert(`Deleted "${b.name}"`, 'success');
          } catch (err) {
            deleteBtn.removeAttribute('disabled');
            showAlert(err instanceof ApiError ? err.message : 'Failed to delete branch');
          }
        },
      },
    });

    const extras = opts.slots?.rowActions?.(b, refresh) ?? [];
    const actionButtons = el('div', {
      class: 'actions',
      children: [...extras, deleteBtn],
    });

    const metaContent = opts.slots?.rowMeta?.(b);
    const defaultMeta = `${formatRelativeTime(b.lastCommitAt)} · ${b.lastAuthor ?? 'unknown'} · ${b.lastCommitMessage}`;
    const metaEl =
      typeof metaContent === 'string' || metaContent === undefined
        ? el('div', { class: 'meta', text: typeof metaContent === 'string' ? metaContent : defaultMeta })
        : metaContent;

    const badges = opts.slots?.rowBadges?.(b) ?? [];

    return el('div', {
      class: 'branch',
      children: [
        checkbox,
        el('div', {
          children: [
            el('span', { class: 'name', text: b.name }),
            b.isProtected ? el('span', { class: 'protected-badge', text: 'protected' }) : null,
            ...badges,
            metaEl,
          ],
        }),
        actionButtons,
      ],
    });
  };

  const renderList = () => {
    if (branches.length === 0) {
      mount(list, [
        el('div', {
          class: 'empty',
          text: opts.emptyMessage ?? 'No branches found.',
        }),
      ]);
      summary.textContent = '';
      updateButtons();
      return;
    }
    summary.textContent = `${branches.length} branches · most recent first`;
    mount(list, branches.map(renderRow));
    updateButtons();
  };

  const refresh = async () => {
    refreshBtn.setAttribute('disabled', '');
    try {
      const data = await opts.source.load();
      branches = data.branches;
      protectedBranch = data.protectedBranch;
      loaded = true;
      for (const name of [...selected]) {
        if (!branches.find((b) => b.name === name)) selected.delete(name);
      }
      renderList();
    } catch (err) {
      showAlert(err instanceof ApiError ? err.message : 'Failed to load branches');
    } finally {
      refreshBtn.removeAttribute('disabled');
    }
  };

  deleteSelectedBtn.addEventListener('click', async () => {
    const targets = [...selected].filter((b) => b !== protectedBranch);
    if (targets.length === 0) return;
    if (!confirm(`Delete ${targets.length} branch${targets.length > 1 ? 'es' : ''}?\n\n${targets.join('\n')}`)) {
      return;
    }
    deleteSelectedBtn.setAttribute('disabled', '');
    try {
      const result = await opts.source.delete(targets);
      const failures = result.results.filter((r) => !r.ok);
      selected.clear();
      await refresh();
      if (failures.length > 0) {
        showAlert(
          `Deleted ${result.results.length - failures.length}/${result.results.length}; failed: ${failures.map((f) => `${f.branch} (${f.error})`).join(', ')}`,
        );
      } else {
        showAlert(`Deleted ${result.results.length} branches`, 'success');
      }
    } catch (err) {
      showAlert(err instanceof ApiError ? err.message : 'Bulk delete failed');
      updateButtons();
    }
  });

  refreshBtn.addEventListener('click', refresh);
  void refresh();

  const toolbarChildren: (HTMLElement | null)[] = [];
  if (opts.slots?.toolbarLeading) toolbarChildren.push(...opts.slots.toolbarLeading);
  toolbarChildren.push(deleteSelectedBtn);
  toolbarChildren.push(el('span', { class: 'spacer' }));
  toolbarChildren.push(summary);
  if (opts.slots?.toolbarTrailing) toolbarChildren.push(...opts.slots.toolbarTrailing);
  toolbarChildren.push(refreshBtn);

  const root = el('div', {
    class: 'card',
    children: [
      el('h2', { text: opts.title }),
      alertBox,
      opts.slots?.headerControls?.length
        ? el('div', { class: 'toolbar header-controls', children: opts.slots.headerControls })
        : null,
      el('div', { class: 'toolbar', children: toolbarChildren }),
      list,
    ],
  });

  return {
    root,
    refresh,
    rerender: () => {
      if (loaded) renderList();
    },
    showAlert,
    getBranches: () => branches,
    getSelected,
    clearSelection: () => {
      selected.clear();
      for (const cb of root.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')) {
        cb.checked = false;
      }
      updateButtons();
    },
  };
}
